/**
 * Collection Create Form with Tag Autocomplete
 */

import { showToast } from './CollectionUI.js';

const MAX_SUGGESTIONS = 10;

const KEY_CODES = {
    ENTER: 'Enter',
    TAB: 'Tab',
    COMMA: ',',
    BACKSPACE: 'Backspace',
    ARROW_UP: 'ArrowUp',
    ARROW_DOWN: 'ArrowDown',
    ESC: 'Escape',
};

const escapeHtml = (unsafe = '') => unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const escapeRegExp = (string = '') => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export class CollectionCreateForm {
    constructor(modal, manager) {
        this.modal = modal;
        this.manager = manager;
        this.currentFdiId = null;
        this.isOpen = false;
        this.isSubmitting = false;

        const limits = window.iphubCollections?.limits || {};
        this.tagLimits = {
            maxTags: Number.parseInt(limits.maxTagsPerCollection ?? 50, 10) || 50,
            maxLength: Number.parseInt(limits.maxTagLength ?? 50, 10) || 50,
        };

        this.tags = [];
        this.lastRemovedTag = null;
        this.tagSuggestions = [];
        this.tagSuggestionsLoaded = false;
        this.activeSuggestionIndex = -1;
        this.suggestionsVisible = false;
        this.suggestionHideTimeout = null;

        this.root = document;
        this.elements = {
            form: this.root.querySelector('[data-create-form]'),
            container: this.root.querySelector('[data-create-container]'),
            formElement: this.root.querySelector('[data-create-form-element]'),
            closeBtn: this.root.querySelector('[data-create-close]'),
            cancelBtn: this.root.querySelector('[data-create-cancel]'),
            submitBtn: this.root.querySelector('[data-create-submit]'),
            nameInput: this.root.querySelector('[data-collection-name-input]'),
            descriptionInput: this.root.querySelector('[data-collection-description-input]'),
            tagsInput: this.root.querySelector('[data-collection-tags-input]'),
            tagsSuggestions: this.root.querySelector('[data-tags-suggestions]'),
            tagsHelper: this.root.querySelector('[data-tags-helper]'),
            tagsFeedback: this.root.querySelector('[data-tags-feedback]'),
            tagsUndoContainer: this.root.querySelector('[data-tags-undo]'),
            tagsUndoBtn: this.root.querySelector('[data-tags-undo-btn]'),
            publicInput: this.root.querySelector('[data-collection-public-input]'),
            nameCounter: this.root.querySelector('[data-name-counter]'),
            tagsPreview: this.root.querySelector('[data-tags-preview]'),
            errorContainer: this.root.querySelector('[data-create-error]'),
            errorText: this.root.querySelector('[data-error-text]'),
            spinner: this.root.querySelector('[data-create-spinner]'),
            submitText: this.root.querySelector('[data-submit-text]'),
        };

        this.defaultSubmitText = this.elements.submitText?.textContent || 'Tạo danh sách';

        this.init();
    }

    init() {
        if (this.elements.closeBtn) {
            this.elements.closeBtn.addEventListener('click', () => this.close());
        }

        if (this.elements.cancelBtn) {
            this.elements.cancelBtn.addEventListener('click', () => this.close());
        }

        if (this.elements.formElement) {
            this.elements.formElement.addEventListener('submit', (event) => {
                event.preventDefault();
                this.handleSubmit();
            });
        }

        if (this.elements.nameInput && this.elements.nameCounter) {
            this.elements.nameInput.addEventListener('input', (event) => {
                this.elements.nameCounter.textContent = event.target.value.length;
            });
        }

        if (this.elements.tagsInput) {
            this.elements.tagsInput.addEventListener('input', (event) => {
                this.handleTagInput(event.target.value);
            });

            this.elements.tagsInput.addEventListener('keydown', (event) => {
                this.handleTagKeydown(event);
            });

            this.elements.tagsInput.addEventListener('paste', (event) => {
                this.handleTagPaste(event);
            });

            this.elements.tagsInput.addEventListener('focus', () => {
                this.renderSuggestions(this.elements.tagsInput.value.trim());
            });

            this.elements.tagsInput.addEventListener('blur', () => {
                this.scheduleSuggestionsHide();
            });
        }

        if (this.elements.tagsSuggestions) {
            this.elements.tagsSuggestions.addEventListener('mousedown', (event) => {
                event.preventDefault();
            });

            this.elements.tagsSuggestions.addEventListener('click', (event) => {
                const option = event.target.closest('[data-tag-option]');
                if (!option) return;
                this.addTag(option.dataset.tagName || '');
                this.clearSuggestions();
                this.elements.tagsInput?.focus();
            });
        }

        if (this.elements.tagsPreview) {
            this.elements.tagsPreview.addEventListener('click', (event) => {
                const removeBtn = event.target.closest('[data-tag-remove]');
                if (!removeBtn) return;
                const tagName = removeBtn.dataset.tagRemove;
                if (tagName) {
                    this.removeTag(tagName, { showUndo: true });
                }
            });
        }

        if (this.elements.tagsUndoBtn) {
            this.elements.tagsUndoBtn.addEventListener('click', () => this.handleUndo());
        }

        document.addEventListener('keydown', (event) => {
            if (event.key === KEY_CODES.ESC && this.isOpen) {
                if (this.suggestionsVisible) {
                    this.clearSuggestions();
                } else {
                    this.close();
                }
            }
        });

        this.updateTagsHelper();
        this.renderTagsPreview();
    }

    open(fdiId = null) {
        if (!this.elements.form) return;

        this.currentFdiId = fdiId;
        this.isOpen = true;
        this.ensureTagSuggestionsLoaded();

        this.elements.form.classList.remove('hidden');
        requestAnimationFrame(() => {
            if (this.elements.container) {
                this.elements.container.classList.remove('translate-y-full', 'scale-95');
                this.elements.container.classList.add('translate-y-0', 'scale-100');
            }
        });

        setTimeout(() => {
            if (this.elements.nameInput) {
                this.elements.nameInput.focus();
            }
        }, 150);
    }

    close() {
        if (!this.elements.form || !this.isOpen) return;

        if (this.elements.container) {
            this.elements.container.classList.remove('translate-y-0', 'scale-100');
            this.elements.container.classList.add('translate-y-full', 'scale-95');
        }

        setTimeout(() => {
            this.elements.form.classList.add('hidden');
            this.isOpen = false;
            this.resetForm();
        }, 200);
    }

    async handleSubmit() {
        if (this.isSubmitting) {
            return;
        }

        const name = this.elements.nameInput?.value.trim();
        if (!name) {
            this.showError('Vui lòng nhập tên danh sách');
            return;
        }

        this.isSubmitting = true;
        this.toggleSubmitState(true, 'Đang tạo...');
        this.hideError();

        const data = {
            name,
            description: this.elements.descriptionInput?.value.trim() || '',
            tags: [...this.tags],
            is_public: Boolean(this.elements.publicInput?.checked),
        };

        try {
            const collection = await this.manager.createCollection(data, {
                autoSelect: Boolean(this.currentFdiId),
            });

            showToast('success', 'Đã tạo danh sách thành công');
            this.close();
            return collection;
        } catch (error) {
            this.showError(error.message || 'Không thể tạo danh sách');
        } finally {
            this.toggleSubmitState(false);
            this.isSubmitting = false;
        }
    }

    toggleSubmitState(isLoading, loadingText = 'Đang xử lý...') {
        if (!this.elements.submitBtn) return;

        this.elements.submitBtn.disabled = isLoading;

        if (this.elements.submitText) {
            this.elements.submitText.textContent = isLoading ? loadingText : this.defaultSubmitText;
        }

        if (this.elements.spinner) {
            this.elements.spinner.classList.toggle('hidden', !isLoading);
        }
    }

    async ensureTagSuggestionsLoaded() {
        if (this.tagSuggestionsLoaded || !this.manager?.api) {
            return;
        }

        try {
            const { tags } = await this.manager.api.getTags();
            if (Array.isArray(tags)) {
                this.tagSuggestions = tags;
            }
        } catch (error) {
            console.warn('Failed to preload collection tags', error);
        } finally {
            this.tagSuggestionsLoaded = true;
        }
    }

    handleTagInput(rawValue = '') {
        const value = rawValue.trim();

        if (rawValue.includes(',')) {
            const parts = rawValue.split(',');
            const remainder = parts.pop();
            parts.forEach((part) => this.addTag(part));
            this.elements.tagsInput.value = remainder.trim();
            this.renderSuggestions(this.elements.tagsInput.value.trim());
            return;
        }

        this.renderSuggestions(value);
        this.clearTagFeedback();
    }

    handleTagKeydown(event) {
        const value = event.target.value.trim();

        switch (event.key) {
            case KEY_CODES.ENTER:
            case KEY_CODES.TAB:
                if (this.suggestionsVisible && this.activeSuggestionIndex >= 0) {
                    event.preventDefault();
                    const option = this.elements.tagsSuggestions?.querySelector('[data-tag-option].is-active');
                    if (option) {
                        this.addTag(option.dataset.tagName || '');
                        this.clearSuggestions();
                        return;
                    }
                }

                if (value) {
                    event.preventDefault();
                    this.addTag(value);
                }
                break;

            case KEY_CODES.COMMA:
                event.preventDefault();
                if (value) {
                    this.addTag(value);
                }
                break;

            case KEY_CODES.BACKSPACE:
                if (!value && this.tags.length) {
                    this.removeTag(this.tags[this.tags.length - 1], { showUndo: true });
                }
                break;

            case KEY_CODES.ARROW_DOWN:
                if (this.suggestionsVisible) {
                    event.preventDefault();
                    this.moveActiveSuggestion(1);
                }
                break;

            case KEY_CODES.ARROW_UP:
                if (this.suggestionsVisible) {
                    event.preventDefault();
                    this.moveActiveSuggestion(-1);
                }
                break;

            case KEY_CODES.ESC:
                this.clearSuggestions();
                break;

            default:
                break;
        }
    }

    handleTagPaste(event) {
        event.preventDefault();
        const text = event.clipboardData.getData('text');
        if (!text) {
            return;
        }
        const parts = text
            .split(/[\,\r\n]+/)
            .map((part) => part.trim())
            .filter(Boolean);

        if (!parts.length) {
            return;
        }

        parts.forEach((part) => this.addTag(part));
        this.elements.tagsInput.value = '';
        this.clearSuggestions();
    }

    addTag(rawTag = '') {
        const normalized = this.normalizeTag(rawTag);
        if (!normalized) {
            return;
        }

        if (normalized.length > this.tagLimits.maxLength) {
            this.showTagFeedback(`Tag vượt quá ${this.tagLimits.maxLength} ký tự.`, 'warning');
            return;
        }

        if (this.tags.length >= this.tagLimits.maxTags) {
            this.showTagFeedback(`Bạn chỉ có thể thêm tối đa ${this.tagLimits.maxTags} tag.`, 'warning');
            return;
        }

        const exists = this.tags.some((existing) => existing.toLowerCase() === normalized.toLowerCase());
        if (exists) {
            this.showTagFeedback('Tag này đã được thêm trước đó.', 'warning');
            return;
        }

        this.tags.push(normalized);
        this.tags.sort((a, b) => a.localeCompare(b, 'vi', { sensitivity: 'base' }));
        this.elements.tagsInput.value = '';
        this.renderTagsPreview();
        this.clearSuggestions();
        this.clearTagFeedback();
        this.hideUndo();
        this.updateTagsHelper();
    }

    removeTag(tagName, { showUndo = false } = {}) {
        const index = this.tags.findIndex((tag) => tag.toLowerCase() === tagName.toLowerCase());
        if (index === -1) {
            return;
        }
        const [removed] = this.tags.splice(index, 1);
        this.renderTagsPreview();
        this.updateTagsHelper();
        if (showUndo && removed) {
            this.lastRemovedTag = removed;
            this.showUndo();
        }
    }

    handleUndo() {
        if (!this.lastRemovedTag) {
            return;
        }
        this.addTag(this.lastRemovedTag);
        this.hideUndo();
    }

    normalizeTag(tag = '') {
        return tag.trim().replace(/\s+/g, ' ');
    }

    renderTagsPreview() {
        if (!this.elements.tagsPreview) {
            return;
        }

        if (!this.tags.length) {
            this.elements.tagsPreview.innerHTML = '';
            return;
        }

        const fragment = document.createDocumentFragment();

        this.tags.forEach((tag) => {
            const badge = document.createElement('span');
            badge.className = 'inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full';
            badge.innerHTML = `${escapeHtml(tag)}
                <button type="button" class="hover:text-blue-900" aria-label="Xóa tag" data-tag-remove="${escapeHtml(tag)}">
                    &times;
                </button>`;
            fragment.appendChild(badge);
        });

        this.elements.tagsPreview.replaceChildren(fragment);
    }

    renderSuggestions(query = '') {
        if (!this.elements.tagsSuggestions) {
            return;
        }

        if (!query) {
            this.clearSuggestions();
            return;
        }

        const lowerQuery = query.toLowerCase();
        const suggestions = this.tagSuggestions
            .filter((item) => {
                if (!item?.name) return false;
                const alreadySelected = this.tags.some((tag) => tag.toLowerCase() === item.name.toLowerCase());
                return !alreadySelected && item.name.toLowerCase().includes(lowerQuery);
            })
            .sort((a, b) => {
                if ((b.count || 0) !== (a.count || 0)) {
                    return (b.count || 0) - (a.count || 0);
                }
                return a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' });
            })
            .slice(0, MAX_SUGGESTIONS);

        if (!suggestions.length) {
            this.clearSuggestions();
            return;
        }

        const fragment = document.createDocumentFragment();

        suggestions.forEach((item, index) => {
            const option = document.createElement('button');
            option.type = 'button';
            option.className = 'flex w-full items-center justify-between px-3 py-2 text-sm text-left hover:bg-blue-50 focus:bg-blue-50';
            option.setAttribute('role', 'option');
            option.dataset.tagOption = 'true';
            option.dataset.tagName = item.name;
            option.innerHTML = `
                <span class="flex-1 text-gray-800">${this.highlightMatch(item.name, query)}</span>
                <span class="ml-2 text-xs text-gray-500">${item.count ?? 0}</span>
            `;
            if (index === 0) {
                option.classList.add('is-active', 'bg-blue-50');
                this.activeSuggestionIndex = 0;
            }
            fragment.appendChild(option);
        });

        this.elements.tagsSuggestions.replaceChildren(fragment);
        this.elements.tagsSuggestions.classList.remove('hidden');
        this.suggestionsVisible = true;
        this.activeSuggestionIndex = 0;
    }

    highlightMatch(text, query) {
        if (!query) {
            return escapeHtml(text);
        }
        const regex = new RegExp(`(${escapeRegExp(query)})`, 'ig');
        return escapeHtml(text).replace(regex, '<mark class="bg-yellow-100 text-yellow-800">$1</mark>');
    }

    moveActiveSuggestion(delta) {
        const options = Array.from(this.elements.tagsSuggestions?.querySelectorAll('[data-tag-option]') || []);
        if (!options.length) {
            return;
        }

        this.activeSuggestionIndex = (this.activeSuggestionIndex + delta + options.length) % options.length;
        options.forEach((option, idx) => {
            const isActive = idx === this.activeSuggestionIndex;
            option.classList.toggle('is-active', isActive);
            option.classList.toggle('bg-blue-50', isActive);
        });
    }

    scheduleSuggestionsHide() {
        if (this.suggestionHideTimeout) {
            clearTimeout(this.suggestionHideTimeout);
        }
        this.suggestionHideTimeout = setTimeout(() => {
            this.clearSuggestions();
        }, 150);
    }

    clearSuggestions() {
        if (!this.elements.tagsSuggestions) {
            return;
        }
        this.elements.tagsSuggestions.classList.add('hidden');
        this.elements.tagsSuggestions.innerHTML = '';
        this.activeSuggestionIndex = -1;
        this.suggestionsVisible = false;
        if (this.suggestionHideTimeout) {
            clearTimeout(this.suggestionHideTimeout);
            this.suggestionHideTimeout = null;
        }
    }

    updateTagsHelper() {
        if (!this.elements.tagsHelper) return;
        const remaining = this.tagLimits.maxTags - this.tags.length;
        this.elements.tagsHelper.textContent = `Nhấn Enter để thêm tag. Còn ${remaining} / ${this.tagLimits.maxTags} tag, tối đa ${this.tagLimits.maxLength} ký tự/tag.`;
    }

    showTagFeedback(message, tone = 'info') {
        if (!this.elements.tagsFeedback) return;
        this.elements.tagsFeedback.textContent = message;
        this.elements.tagsFeedback.classList.remove('hidden');
        this.elements.tagsFeedback.classList.toggle('text-amber-600', tone !== 'error');
        this.elements.tagsFeedback.classList.toggle('text-red-600', tone === 'error');
    }

    clearTagFeedback() {
        if (!this.elements.tagsFeedback) return;
        this.elements.tagsFeedback.textContent = '';
        this.elements.tagsFeedback.classList.add('hidden');
        this.elements.tagsFeedback.classList.remove('text-red-600');
    }

    showUndo() {
        if (!this.elements.tagsUndoContainer || !this.lastRemovedTag) return;
        this.elements.tagsUndoContainer.classList.remove('hidden');
    }

    hideUndo() {
        if (!this.elements.tagsUndoContainer) return;
        this.elements.tagsUndoContainer.classList.add('hidden');
        this.lastRemovedTag = null;
    }

    showError(message) {
        if (!this.elements.errorContainer || !this.elements.errorText) {
            return;
        }
        this.elements.errorText.textContent = message;
        this.elements.errorContainer.classList.remove('hidden');
    }

    hideError() {
        if (this.elements.errorContainer) {
            this.elements.errorContainer.classList.add('hidden');
        }
    }

    resetForm() {
        this.currentFdiId = null;
        this.hideError();
        this.clearTagFeedback();
        this.hideUndo();
        this.tags = [];
        this.renderTagsPreview();
        this.updateTagsHelper();
        this.clearSuggestions();
        if (this.elements.formElement) {
            this.elements.formElement.reset();
        }
        if (this.elements.nameCounter) {
            this.elements.nameCounter.textContent = '0';
        }
        if (this.elements.tagsInput) {
            this.elements.tagsInput.value = '';
        }
    }
}

export default CollectionCreateForm;
